import { useEffect, useId, useState, type ReactNode } from 'react';
import { Dialog } from './Dialog';
import { Button } from './ui/Button';

/**
 * Generic confirmation dialog with optional type-to-confirm safety net.
 *
 * Use the `requireText` prop for irreversible destructive actions (delete
 * domain / link / workspace etc.) — the confirm button stays disabled until
 * the user types the exact string. Mirrors the GitHub / Stripe / Linear
 * convention.
 *
 * Visual: glass panel via the shared <Dialog> primitive; cancel + confirm
 * are <Button> primitives so destructive styling and pill shape stay
 * consistent with the rest of the app.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  requireText,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  requireText?: string;
}) {
  const titleId = useId();
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reset state every time the dialog opens.
  useEffect(() => {
    if (open) {
      setTyped('');
      setBusy(false);
      setErr(null);
    }
  }, [open]);

  const matched = !requireText || typed === requireText;

  async function handleConfirm() {
    if (!matched || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await onConfirm();
      onClose();
    } catch (e) {
      setErr((e as Error).message || 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={busy ? () => {} : onClose} labelledBy={titleId} dismissible={!busy}>
      <h2 id={titleId} className="font-display text-xl font-bold tracking-tight">
        {title}
      </h2>
      {description && (
        <div className="mt-2 text-sm text-[var(--color-on-surface-variant)]">{description}</div>
      )}

      {requireText && (
        <div className="mt-5">
          <label className="block text-xs font-semibold uppercase tracking-[0.06em] text-[var(--color-on-surface-variant)]">
            Type{' '}
            <code className="rounded bg-[var(--color-surface-container)] px-1.5 py-0.5 font-mono text-xs text-[var(--color-foreground)]">
              {requireText}
            </code>{' '}
            to confirm
          </label>
          <input
            type="text"
            autoFocus
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && matched) handleConfirm();
            }}
            className="mt-1.5 w-full rounded-xl border border-[var(--color-border)] bg-[var(--surface-deep)] px-3 py-2.5 font-mono text-sm outline-none transition focus:border-[var(--color-ring)]"
            placeholder={requireText}
          />
        </div>
      )}

      {err && <p className="mt-3 text-sm text-[var(--color-destructive)]">{err}</p>}

      <div className="mt-6 flex items-center justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          {cancelLabel}
        </Button>
        <Button
          variant={destructive ? 'destructive' : 'primary'}
          onClick={handleConfirm}
          disabled={!matched || busy}
          autoFocus={!requireText}
        >
          {busy ? 'Working…' : confirmLabel}
        </Button>
      </div>
    </Dialog>
  );
}
